/**
 * Show-Me / Tell-Me flashcard deck — swipe-style learning surface.
 *
 * Mobile-first. Single card visible. Tap to flip. Forward/back to navigate.
 * Filter by Tell Me / Show Me.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, RefreshCw, Loader2, ArrowLeft, Mic, Eye } from "lucide-react";
import { Button } from "../components/ui/button";
import CalmEmptyState from "../components/learner/CalmEmptyState";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ShowTellDeck() {
  const nav = useNavigate();
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState("all"); // all | tell_me | show_me
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const url = filter === "all" ? "/api/show-tell" : `/api/show-tell?type=${filter}`;
        const { data } = await axios.get(`${API}${url}`);
        setCards(data.cards || []);
        setIdx(0);
        setFlipped(false);
      } finally {
        setLoading(false);
      }
    })();
  }, [filter]);

  if (loading) {
    return (
      <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  const card = cards[idx];
  const total = cards.length;

  const next = () => { setFlipped(false); setIdx((i) => (i + 1) % total); };
  const prev = () => { setFlipped(false); setIdx((i) => (i - 1 + total) % total); };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] pb-12">
      <div className="sticky top-0 z-10 bg-slate-50/95 dark:bg-[#0c1120]/95 backdrop-blur border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            data-testid="show-tell-back"
            onClick={() => nav("/learn")}
            className="text-slate-500 hover:text-slate-900 dark:hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-display text-base md:text-lg font-bold text-slate-900 dark:text-white">
            Show Me / Tell Me
          </h1>
          <span className="ml-auto text-[11px] tabular-nums font-bold text-slate-500">
            {total ? idx + 1 : 0}/{total}
          </span>
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 pt-5">
        {/* Filter chips */}
        <div className="flex gap-2 mb-5">
          {[
            { id: "all", label: "All cards" },
            { id: "tell_me", label: "Tell Me", icon: Mic },
            { id: "show_me", label: "Show Me", icon: Eye },
          ].map(({ id, label, icon: Icon }) => {
            const on = filter === id;
            return (
              <button
                key={id}
                data-testid={`filter-${id}`}
                onClick={() => setFilter(id)}
                className={`text-[12.5px] font-bold rounded-full px-3 py-1.5 inline-flex items-center gap-1.5 transition ${
                  on
                    ? "bg-amber-500 text-slate-900"
                    : "bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" />}
                {label}
              </button>
            );
          })}
        </div>

        {/* Card */}
        {card ? (
          <button
            data-testid="show-tell-card"
            onClick={() => setFlipped((f) => !f)}
            className="w-full text-left bg-white dark:bg-slate-900 rounded-3xl border-2 border-amber-200 dark:border-amber-700/40 shadow-sm p-7 min-h-[340px] flex flex-col justify-between active:scale-[0.99] transition"
          >
            <div>
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] font-bold text-amber-600 dark:text-amber-400 mb-3">
                {card.type === "tell_me" ? <><Mic className="w-3 h-3" /> Tell Me</> : <><Eye className="w-3 h-3" /> Show Me</>}
              </span>
              {!flipped ? (
                <p data-testid="show-tell-question" className="text-lg md:text-xl font-display font-semibold text-slate-900 dark:text-white leading-snug">
                  {card.question}
                </p>
              ) : (
                <p data-testid="show-tell-answer" className="text-[15px] text-slate-700 dark:text-slate-200 leading-relaxed">
                  {card.answer}
                </p>
              )}
            </div>
            <div className="mt-5 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400">
              <RefreshCw className="w-3 h-3" />
              {flipped ? "Tap to see question" : "Tap to reveal answer"}
            </div>
          </button>
        ) : (
          <CalmEmptyState
            testid="show-tell-empty"
            title={
              filter === "all"
                ? "No Show Me / Tell Me cards yet"
                : `No ${filter === "tell_me" ? "Tell Me" : "Show Me"} cards in this filter`
            }
            body={
              filter === "all"
                ? "These DVSA question cards will appear here when seeded."
                : "Try the All cards filter to see everything available."
            }
            cta={filter === "all" ? null : () => setFilter("all")}
            ctaLabel="Show all cards"
          />
        )}

        {/* Nav */}
        {total > 0 && (
          <div className="grid grid-cols-2 gap-3 mt-5">
            <Button
              data-testid="show-tell-prev"
              variant="outline"
              onClick={prev}
              className="h-12 rounded-full"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <Button
              data-testid="show-tell-next"
              onClick={next}
              className="h-12 rounded-full bg-slate-900 text-white dark:bg-white dark:text-slate-900 font-bold"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
